// frontend/src/pages/AuthPage/AuthPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import { TextField, Button, Box, InputAdornment, IconButton, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { CircularProgress } from "@mui/material";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';
import validator from 'validator';
import fpPromise from '@fingerprintjs/fingerprintjs';
import { useGoogleLogin } from '@react-oauth/google';
import './AuthPage.css';
import { Helmet } from 'react-helmet-async';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import UniversalNav from '../../components/UniversalNav/UniversalNav';

const promoSlidesLogo = [
    {
      logo: "/static/vetraai_logo_grey.png"
    },
    {
      logo: "/static/ai_logo.png"
    },
    {
      logo: "/static/chatbot_logo_vetraai.png"
    }
];

const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const AuthPage = () => {
  const { t, i18n } = useTranslation();
  const promoSlides = t('authPage.promoSlides', { returnObjects: true });
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [slideInterval, setSlideInterval] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showResendLink, setShowResendLink] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { loginWithRedirect, user: auth0User, isAuthenticated: isAuth0Authenticated } = useAuth0();
  const showGuestLink = process.env.REACT_APP_GUEST_AUTH_ENABLED === 'true';
  const showEnterpriseSSO = process.env.REACT_APP_ENABLE_ENTERPRISE_SSO === 'true';
  const [isScrolled, setIsScrolled] = useState(false);
  const containerRef = useRef(null);

  const handleScroll = (e) => {
      if (e.target.scrollTop > 20) {
          setIsScrolled(true);
      } else {
          setIsScrolled(false);
      }
  };

  // Particle background logic
  const particlesInit = useCallback(async (engine) => {
      await loadSlim(engine);
  }, []);

  const particlesConfig = {
      background: { color: { value: 'transparent' } },
      fpsLimit: 120,
      interactivity: {
          events: { onHover: { enable: true, mode: 'repulse' }, resize: true },
          modes: { repulse: { distance: 100, duration: 0.4 } },
      },
      particles: {
          color: { value: '#ffffff' },
          links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
          move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
          number: { density: { enable: true, area: 800 }, value: 80 },
          opacity: { value: 0.5 },
          shape: { type: 'circle' },
          size: { value: { min: 1, max: 3 } },
      },
      detectRetina: true,
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % promoSlides.length);
    }, 8000);
    setSlideInterval(interval);
    return () => clearInterval(interval);
  }, [promoSlides.length]);

  useEffect(() => {
    const verifiedParam = searchParams.get('verified');
    const errorParam = searchParams.get('error');

    if (verifiedParam === 'false') {
        setNotification({ message: t('authPage.notifications.verificationFailed'), type: 'error' });
        setSearchParams({}, { replace: true });
    } else if (errorParam === 'auth_failed') {
        setNotification({ message: t('authPage.notifications.authFailed'), type: 'error' });
        setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const handleSlideSelect = (index) => {
    setCurrentSlide(index);
    if (slideInterval) clearInterval(slideInterval);
    const newInterval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % promoSlides.length);
    }, 8000);
    setSlideInterval(newInterval);
  };

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.message]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setNotification({ message: '', type: '' });
    setShowResendLink(false);

    if (!isLogin && password !== confirmPassword) {
        setNotification({ message: t('authPage.notifications.passwordsMismatch'), type: 'error' });
        return;
    }
    if (!isLogin && passwordError) {
        setNotification({ message: t('authPage.notifications.passwordInvalid'), type: 'error' });
        return;
    }
    if (!isLogin && !agreedToTerms) {
        setNotification({ message: t('authPage.notifications.termsRequired'), type: 'error' });
        return;
    }
    
    setIsLoading(true);

    // --- Generate Device Fingerprint only if registering ---
    let deviceFingerprint = null;
    if (!isLogin) {
        try {
            const fp = await fpPromise.load();
            const result = await fp.get();
            deviceFingerprint = result.visitorId;
        } catch (fpError) {
            console.warn("Could not generate hardware fingerprint (e.g., blocked by extension):", fpError);
        }
    }

    const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
    // Get the current language code (e.g., 'it', 'en', 'fr')
    // We split by '-' to handle codes like 'en-US' and just get 'en'
    const currentLang = i18n.language.split('-')[0];
    const payload = isLogin 
        ? { email, password } 
        : { email, password, fullName, agreedToTerms, device_fingerprint: deviceFingerprint, language: currentLang };
    
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (jsonErr) {
        // If response is HTML (server down / 502), throw a friendly error
        throw new Error('SERVER_OFFLINE');
      }
      
      if (!response.ok) {
        if (response.status === 403 && data.message && data.message.includes('Account not verified')) {
            setShowResendLink(true);
        }
        throw new Error(data.message || `An error occurred: ${response.statusText}`);
      }

      if (isLogin) {
        window.location.href = "/initializing";
      } else {
        setIsLogin(true);
        setNotification({ 
            message: t('authPage.notifications.registrationSuccess'), 
            type: 'success' 
        });
      }
    } catch (err) {
      // --- Map backend codes to frontend translations ---
      let displayMessage = err.message;
      
      // Handle network errors or offline server gracefully
      if (
        err.message === 'SERVER_OFFLINE' || 
        err.message.includes('Failed to fetch') || 
        err.message.includes('NetworkError') || 
        err.message.includes('Unexpected token')
      ) {
          displayMessage = t('authPage.notifications.serverUnavailable', 'Unable to connect to the server. Please try again in a few moments.');
      } else if (err.message === 'GOOGLE_LOGIN_REQUIRED') {
          displayMessage = t('authPage.notifications.googleLoginRequired');
      } else if (err.message === 'INVALID_CREDENTIALS') {
          displayMessage = t('authPage.notifications.invalidCredentials');
      }
      
      setNotification({ message: displayMessage, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize the custom Google Login hook
  const loginWithGoogle = useGoogleLogin({
      onSuccess: async (tokenResponse) => {
          // The custom hook returns an access_token, not a credential ID token.
          // We need to fetch the user info from Google manually using this token.
          setIsLoading(true);
          setNotification({ message: '', type: '' });
          
          try {
              // 1. Fetch user profile from Google using the access token
              const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                  headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
              });
              
              if (!userInfoResponse.ok) throw new Error("Failed to fetch Google profile.");
              const userInfo = await userInfoResponse.json();

              // 2. Send the profile data to our Flask backend (reusing our existing route logic)
              const response = await fetch('/api/auth/google', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  // We simulate the credential structure so we don't have to rewrite the backend!
                  body: JSON.stringify({ 
                      email: userInfo.email,
                      full_name: userInfo.name,
                      google_id: userInfo.sub
                  }),
              });
              
              let data = {};
              try {
                  data = await response.json();
              } catch (jsonErr) {
                  throw new Error('SERVER_OFFLINE');
              }
              if (!response.ok) throw new Error(data.message || 'Google authentication failed.');

              window.location.href = "/initializing"; 
              
          } catch (err) {
              let displayMessage = err.message;
              if (err.message === 'SERVER_OFFLINE' || err.message.includes('Failed to fetch')) {
                  displayMessage = t('authPage.notifications.serverUnavailable', 'Unable to connect to the server. Please try again in a few moments.');
              }
              setNotification({ message: displayMessage, type: 'error' });
              setIsLoading(false);
          }
      },
      onError: () => setNotification({ message: 'Google Login failed.', type: 'error' }),
  });

  const handleResendVerification = async () => {
      setIsLoading(true);
      setNotification({ message: '', type: '' });
      try {
          const response = await fetch('/api/auth/resend-verification', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ email: email }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.message);
          setNotification({ message: data.message, type: 'success' });
          setShowResendLink(false);
      } catch (err) {
          setNotification({ message: err.message, type: 'error' });
      } finally {
          setIsLoading(false);
      }
  };

  const validatePassword = (value) => {
    setPassword(value);
    if (validator.isStrongPassword(value, { minLength: 8, minLowercase: 1, minUppercase: 1, minNumbers: 1, minSymbols: 1 })) {
        setPasswordError('');
    } else {
        setPasswordError(t('authPage.notifications.passwordStrength'));
    }
  };

  // --- handleGuestLogin ---
  const handleGuestLogin = async () => {
      setNotification({ message: '', type: '' });
      setIsLoading(true);
      try {
        // Check localStorage for an existing persistent ID
        const persistentId = localStorage.getItem('persistentGuestId');
        const payload = persistentId ? { persistent_guest_id: persistentId } : {};

        const response = await fetch('/api/auth/guest-login', { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }, // We are sending a body now
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        let data = {};
        try {
            data = await response.json();
        } catch (jsonErr) {
            throw new Error('SERVER_OFFLINE');
        }
        if (!response.ok) {
          throw new Error(data.message || 'Could not log in as guest.');
        }

        // If the backend sent back a *new* persistent ID, save it.
        if (data.persistent_guest_id) {
            localStorage.setItem('persistentGuestId', data.persistent_guest_id);
        }

        // Redirect to force a reload and let AppContext detect the new session.
        window.location.href = "/initializing"; 
      } catch (err) {
        let displayMessage = err.message;
        if (err.message === 'SERVER_OFFLINE' || err.message.includes('Failed to fetch')) {
            displayMessage = t('authPage.notifications.serverUnavailable', 'Unable to connect to the server. Please try again in a few moments.');
        }
        setNotification({ message: displayMessage, type: 'error' });
      } finally {
        setIsLoading(false);
      }
  };
  
  // ... (rest of the component is unchanged) ...
  const toggleForm = () => {
    setIsLogin(!isLogin);
    setNotification({ message: '', type: '' });
  };
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleOpenForgotPassword = () => setOpenForgotPasswordModal(true);
  const handleCloseForgotPassword = () => setOpenForgotPasswordModal(false);
  const handleForgotPasswordSubmit = async () => {
    if (!validator.isEmail(forgotPasswordEmail)) {
      setNotification({ message: 'Please enter a valid email address.', type: 'error' });
      return;
    }
    setIsLoading(true);
    setNotification({ message: '', type: '' });
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });
      let data = {};
      try {
        data = await response.json();
      } catch (jsonErr) {
        throw new Error('SERVER_OFFLINE');
      }
      if (!response.ok) throw new Error(data.message || 'An error occurred.');
      let displayMessage = data.message;
      if (data.message === 'PASSWORD_RESET_SENT') {
          displayMessage = t('authPage.notifications.passwordResetSent');
      }
      setNotification({ message: displayMessage, type: 'success' });
    } catch (err) {
      let displayMessage = err.message;
      if (err.message === 'SERVER_OFFLINE' || err.message.includes('Failed to fetch')) {
          displayMessage = t('authPage.notifications.serverUnavailable', 'Unable to connect to the server. Please try again in a few moments.');
      }
      setNotification({ message: displayMessage, type: 'error' });
    } finally {
      setIsLoading(false);
      handleCloseForgotPassword();
    }
  };

    // 1. Trigger the redirect when button is clicked
    const handleEnterpriseSSO = () => {
        loginWithRedirect({
            authorizationParams: {
                connection: 'vetra-ai-microsoft' // The exact name from your Auth0 connection!
            }
        });
    };

    // 2. Catch the redirect when Auth0 sends them back
    useEffect(() => {
        const handleSSOHandshake = async () => {
            if (isAuth0Authenticated && auth0User) {
                setIsLoading(true);
                try {
                    const response = await fetch('/api/auth/sso-login', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ 
                            email: auth0User.email,
                            full_name: auth0User.name || '',
                            sso_provider: 'auth0-enterprise',
                            sso_id: auth0User.sub
                        }),
                    });
                    
                    let data = {};
                    try {
                        data = await response.json();
                    } catch (jsonErr) {
                        throw new Error('SERVER_OFFLINE');
                    }
                    
                    if (!response.ok) throw new Error(data.message || "SSO Login failed on backend.");
                    
                    window.location.href = "/initializing"; 
                } catch (err) {
                    let displayMessage = err.message;
                    if (err.message === 'SERVER_OFFLINE' || err.message.includes('Failed to fetch')) {
                        displayMessage = t('authPage.notifications.serverUnavailable', 'Unable to connect to the server. Please try again in a few moments.');
                    }
                    setNotification({ message: displayMessage, type: 'error' });
                    setIsLoading(false);
                }
            }
        };
        
        handleSSOHandshake();
    }, [isAuth0Authenticated, auth0User, t]);

  return (
    // A React fragment is needed to hold both the nav and the content
    <div 
        ref={containerRef}
        onScroll={handleScroll}
        className={`auth-page-root ${isScrolled ? 'scrolled' : ''}`}
    >
        <Particles id="tsparticles-auth" init={particlesInit} options={particlesConfig} />
        <Helmet>
            <title>{isLogin ? t('authPage.helmet.loginTitle') : t('authPage.helmet.registerTitle')}</title>
            <meta name="description" content={t('authPage.helmet.description')} />
        </Helmet>
        <UniversalNav />
        <main className="auth-page-container">
          <div className="auth-panel">
              <div className="auth-promo-side">
                  <div className="promo-content">
                      <img src={promoSlidesLogo[currentSlide].logo} alt="App Logo" className="promo-logo" />
                      <h2>{promoSlides[currentSlide].title}</h2>
                      <p>{promoSlides[currentSlide].text}</p>
                  </div>
                  <div className="promo-dots">
                      {promoSlides.map((_, index) => (
                      <span key={index} className={`dot ${index === currentSlide ? 'active' : ''}`} onClick={() => handleSlideSelect(index)}></span>
                      ))}
                  </div>
              </div>
              <div className="auth-form-side">
                  <div className="form-wrapper">
                      <Box component="form" onSubmit={handleAuthSubmit} sx={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <h2>{isLogin ? t('authPage.login.title') : t('authPage.register.title')}</h2>
                          <p className="subtitle">{isLogin ? t('authPage.login.subtitle') : t('authPage.register.subtitle')}</p>
                          {!isLogin && (
                              <TextField label={t('authPage.form.fullNameLabel')} variant="outlined" fullWidth margin="dense" value={fullName} onChange={(e) => setFullName(e.target.value)} required inputProps={{ spellCheck: 'false' }} InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ color: 'var(--secondary-color)' }} /></InputAdornment> }} />
                          )}
                          <TextField label={t('authPage.form.emailLabel')} type="email" variant="outlined" fullWidth margin="dense" value={email} onChange={(e) => setEmail(e.target.value)} required inputProps={{ spellCheck: 'false' }} InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: 'var(--secondary-color)' }} /></InputAdornment> }} />
                          <TextField label={t('authPage.form.passwordLabel')} variant="outlined" fullWidth margin="dense" value={password} onChange={(e) => validatePassword(e.target.value)} required type={showPassword ? 'text' : 'password'} error={!isLogin && !!passwordError} helperText={!isLogin && passwordError} InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'var(--secondary-color)' }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">{showPassword ? <VisibilityOff sx={{ color: 'var(--secondary-color)' }} /> : <Visibility sx={{ color: 'var(--secondary-color)' }} />}</IconButton></InputAdornment> }} />
                          {!isLogin && (
                              <TextField label={t('authPage.form.confirmPasswordLabel')} variant="outlined" fullWidth margin="dense" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required type={showConfirmPassword ? 'text' : 'password'} InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'var(--secondary-color)' }} /></InputAdornment>, endAdornment: ( <InputAdornment position="end"><IconButton aria-label="toggle confirm password visibility" onClick={() => setShowConfirmPassword((show) => !show)} onMouseDown={handleMouseDownPassword} edge="end">{showConfirmPassword ? <VisibilityOff sx={{ color: 'var(--secondary-color)' }} /> : <Visibility sx={{ color: 'var(--secondary-color)' }} />}</IconButton></InputAdornment> ) }} />
                          )}
                          {!isLogin && (
                            <FormControlLabel 
                                control={<Checkbox checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} name="agreedToTerms" color="primary" />}
                                label={
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        <Trans i18nKey="authPage.form.termsLabel">
                                            I have read and agree to the <Link component={RouterLink} to="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                                        </Trans>
                                    </Typography>
                                } 
                                sx={{ mt: 1 }} 
                            />
                          )}
                          {isLogin && (
                              <div style={{ width: '100%', textAlign: 'right', marginTop: '0.5rem', marginBottom: '1rem' }}>
                                  <span onClick={handleOpenForgotPassword} className="toggle-form" style={{ cursor: 'pointer', fontWeight: 'normal' }}>{t('authPage.form.forgotPassword')}</span>
                              </div>
                          )}
                          {notification.message && (
                              <Alert severity={notification.type} sx={{ width: '100%', mt: 2, boxSizing: 'border-box', alignItems: 'center'}}>
                                  {notification.message}
                                  {showResendLink && ( <span onClick={handleResendVerification} style={{ textDecoration: 'underline', cursor: 'pointer', marginLeft: '1rem', fontWeight: 'bold' }}>{t('authPage.notifications.resendVerificationPrompt')}</span> )}
                              </Alert>
                          )}
                          <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}>
                              {isLoading ? (
                                  <>
                                      <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                      {t('authPage.buttons.processing')}
                                  </>
                              ) : (
                                  isLogin ? t('authPage.login.button') : t('authPage.register.button')
                              )}
                          </Button>
                          <p className="toggle-form" style={{textAlign: 'center', width: '100%', marginTop: '1rem'}}>
                              {isLogin ? t('authPage.login.togglePrompt') : t('authPage.register.togglePrompt')}
                              <span onClick={toggleForm}>{isLogin ? t('authPage.login.toggleLink') : t('authPage.register.toggleLink')}</span>
                          </p>
                          <div className="guest-login-separator"><span>{t('authPage.guest.separator')}</span></div>
                          <Button 
                              variant="outlined" 
                              fullWidth 
                              onClick={() => loginWithGoogle()}
                              disabled={isLoading} 
                              startIcon={<GoogleIcon />}
                              sx={{ 
                                  py: 1.2, 
                                  textTransform: 'none', 
                                  fontSize: '1rem',
                                  color: 'rgba(255, 255, 255, 0.85)',
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  '&:hover': {
                                      borderColor: 'white',
                                      backgroundColor: 'white'
                                  }
                              }}
                          >
                              {t('authPage.google.continue')}
                          </Button>

                        {/* THE FEATURE-FLAGGED ENTERPRISE SSO BUTTON */}
                        {showEnterpriseSSO && (
                            <Button 
                                variant="outlined" 
                                fullWidth 
                                onClick={handleEnterpriseSSO}
                                disabled={isLoading} 
                                startIcon={<BusinessIcon />}
                                sx={{ 
                                    mt: 2,
                                    py: 1.2, 
                                    textTransform: 'none', 
                                    fontSize: '1rem',
                                    color: 'rgba(255, 255, 255, 0.85)',
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    '&:hover': {
                                        borderColor: 'white',
                                        backgroundColor: 'white',
                                        color: '#0d1218'
                                    }
                                }}
                            >
                                {t('authPage.sso.continue')}
                            </Button>
                        )}

                        {showGuestLink && (
                            <>
                                <div className="guest-login-separator"><span>{t('authPage.guest.separator')}</span></div>
                                <Button 
                                    variant="outlined" 
                                    fullWidth 
                                    onClick={handleGuestLogin}
                                    disabled={isLoading} 
                                    sx={{ 
                                        py: 1.2, 
                                        textTransform: 'none', 
                                        fontSize: '1rem',
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        '&:hover': {
                                            borderColor: 'white',
                                            backgroundColor: 'white',
                                            color: '#0d1218' /* Makes text dark when background turns white */
                                        }
                                    }}
                                >
                                    {t('authPage.guest.continue')}
                                </Button>
                            </>
                        )}
                      </Box>
                  </div>
              </div>
          </div>
          <Dialog 
              open={openForgotPasswordModal} 
              onClose={handleCloseForgotPassword}
              className="forgot-password-dialog" // This class is for styling
          >
              <DialogTitle>{t('authPage.forgotPasswordModal.title')}</DialogTitle>
              <DialogContent>
                  <DialogContentText>
                      {/* The descriptive text goes here */}
                      {t('authPage.forgotPasswordModal.description')}
                  </DialogContentText>
                  <TextField 
                      autoFocus 
                      margin="dense" 
                      id="forgot-email" 
                      // The label for the input field goes here
                      label={t('authPage.forgotPasswordModal.emailLabel')} 
                      type="email" 
                      fullWidth 
                      variant="standard" 
                      value={forgotPasswordEmail} 
                      onChange={(e) => setForgotPasswordEmail(e.target.value)} 
                  />
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleCloseForgotPassword}>
                      {t('authPage.forgotPasswordModal.cancelButton')}
                  </Button>
                  {/* Wrap the action text in a real, clickable Button */}
                  <Button 
                      onClick={handleForgotPasswordSubmit} 
                      disabled={isLoading}
                      variant="contained" // This makes it the primary action button
                  >
                      {isLoading 
                          ? t('authPage.forgotPasswordModal.sendingButton') 
                          : t('authPage.forgotPasswordModal.sendButton')}
                  </Button>
              </DialogActions>
          </Dialog>
      </main>
  </div>
  );
};

export default AuthPage;